/**
 * Ürün türü ve türe bağlı yüklemler.
 *
 * Tek tip tablosu (CLAUDE.md): kod 28 yerde `kind === 'JUG_19L'` eşitliği yazıyordu ve bu
 * eşitlik "iade edilebilir damacana mı?" sorusunun yerine geçmişti. Üçüncü tür eklendiğinde
 * bu eşitlikler 15L'yi sessizce dışarıda bırakıyordu: rapor tutar görünür, 15L hiç sayılmaz.
 *
 * Bu modül o soruları TEK yere toplar. Yeni tür eklendiğinde derleme kırılsın diye tablo
 * `satisfies Record<ProductKind, ...>` ile yazılır; bir satır eksik bırakılırsa `tsc`
 * `Property 'JUG_15L_GLASS' is missing in type` hatası verir ve kimse karar vermeden geçemez.
 *
 * NOT: Prisma'nın kendi `ProductKind` enum'u bu sabitlerle AYNI adda olmalı. API köprüsü
 * (apps/api/src/common/product-kind-bridge.ts) bu eşitliği derleme zamanında denetler;
 * biri diğerinden ayrışırsa köprü düşer.
 */

export const PRODUCT_KINDS = ['JUG_19L', 'JUG_15L_GLASS', 'CASE_05L'] as const;
export type ProductKind = (typeof PRODUCT_KINDS)[number];

/**
 * Bir tür için türe bağlı yüklemler.
 *
 * `returnsEmpty`: tür iade edilebilir (boş dönüşü olan) bir damacana mı? Prisma'daki
 * `Product.returnsEmpty` artık bu yüklemden türetilir; bayrak kolonda duruyor ama
 * uygulama kodu onu hesaplamıyor — bkz. `docs/adr/2026-09-20-15l-cam-urun-turu.md`.
 *
 * `foreignOffsetsDeposit`: yabancı marka boş, bu türün depozitosunu düşürür mü? 19L'de
 * evet (plastik 19L = plastik 19L); 15L cam'da hayır (kapı sonucu tasarımı §1.3).
 * K2 formülünün tek girdisi budur; 19L plastik + Erikli 19L plastik aynı kova, 15L cam
 * + Erikli 19L plastik farklı kova. CASE_05L için anlamsız; koli damacana değildir.
 *
 * `inEquation`: tür kapanış denkleminde kendi satırıyla bulunur mu? Üçü de evet:
 * 19L, 15L ve koli. "Bakiye kolonu" defterden hesaplanır (kural 1) — koli
 * denklemden dışlanmaz, çünkü şoför bir koli açıp içinden şişe dağıtamaz (D3).
 * Prisma `in` sorgusuna geçilebilir.
 *
 * Yeni alan eklenirse burada BİR satır eklenecek; eksik bırakılırsa `satisfies` ile
 * derleme kırılır.
 */
export const PRODUCT_KIND_TRAITS = {
  JUG_19L: { returnsEmpty: true, foreignOffsetsDeposit: true, inEquation: true },
  JUG_15L_GLASS: { returnsEmpty: true, foreignOffsetsDeposit: false, inEquation: true },
  CASE_05L: { returnsEmpty: false, foreignOffsetsDeposit: false, inEquation: true },
} as const satisfies Record<
  ProductKind,
  { returnsEmpty: boolean; foreignOffsetsDeposit: boolean; inEquation: boolean }
>;

/** Yabancı marka boş, bu türün depozitosunu kapatır mı? */
export function foreignOffsetsDeposit(kind: ProductKind): boolean {
  return PRODUCT_KIND_TRAITS[kind].foreignOffsetsDeposit;
}

/** Tür iade edilebilir bir damacana mı? Prisma `kind: { in: ... }` sorgularında kullanılır. */
export function returnsEmpty(kind: ProductKind): boolean {
  return PRODUCT_KIND_TRAITS[kind].returnsEmpty;
}

/** Tür kapanış denkleminde kendi satırıyla mı bulunur? */
export function inEquation(kind: ProductKind): boolean {
  return PRODUCT_KIND_TRAITS[kind].inEquation;
}

/**
 * İade edilebilir türlerin listesi. Prisma `in` sorgusuna doğrudan geçilebilir.
 *
 * Liste `PRODUCT_KIND_TRAITS`'ten TÜRETİLİR, elle yazılmaz: yeni bir tür oraya
 * `returnsEmpty: true` ile eklenirse buraya kendiliğinden girer ve ayrıca bir satır
 * eklemek gerekmez. Elle yazılmış ikinci bir liste, tablo ile sessizce ayrışabilecek
 * tek yer olurdu.
 */
export const RETURNABLE_JUG_KINDS: readonly ProductKind[] = PRODUCT_KINDS.filter(
  (k) => PRODUCT_KIND_TRAITS[k].returnsEmpty,
);

/**
 * Kapanış denklemine giren türler. Prisma `in` sorgusuna doğrudan geçilebilir.
 *
 * `PRODUCT_KIND_TRAITS`'ten türetilir; yeni tür `inEquation: true` ile eklendiyse
 * buraya kendiliğinden girer. P1 (koli denklemi) ile birlikte CASE_05L bu listeye
 * dahil oldu — koli damacana değildir ama "açıp dağıtma" yok, her zaman tam koli
 * satılır/döner (D3). Geriye uyum için `closingEquation` JSON'una yazılan
 * `equationKinds` alanı bu listenin tıkız kopyasıdır; türetildiği kaynaktan
 * denetlenebilmesi için türetildiği yerde durur, ayrı yerde elle yazılmaz.
 */
export const EQUATION_KINDS: readonly ProductKind[] = PRODUCT_KINDS.filter(
  (k) => PRODUCT_KIND_TRAITS[k].inEquation,
);

/**
 * Kanal adından tür tahmini (master-data import + eşleme kartı önerisi).
 *
 * Mevcut ürünlerle eşleşmeyen kanalsı ad ilk açılışta tür seçmeden ürün açabilsin diye.
 * ÜÇÜ DE EŞLEŞİRSE: ilk bulan kazanır (öncelik JUG_15L_GLASS > JUG_19L > CASE_05L). Eşleşen
 * yoksa `null` döner; sunucu ürünü AÇMAZ, rapora `skippedUnknownKind` olarak düşer. Ürün
 * tahminle açılırken kullanıcının manuel onayı hâlâ zorunlu: formda tür açılır listeden
 * değiştirilebilir.
 *
 * `i` bayrağı YOK: büyük/küçük harf farkı, nokta/virgül ve ondalık varyasyonları panelde
 * olduğu gibi kabul edilir. Sıralama kasıtlı: kullanıcının görmediği varsayılan ürün
 * açılırken bir tür seçilmemişse ürün YOK, rapor VAR.
 */
export function guessProductKind(name: string): ProductKind | null {
  const lower = name.toLocaleLowerCase('tr-TR');
  if (/\b15\s*(l|lt|litre)\b/.test(lower)) return 'JUG_15L_GLASS';
  if (/\b19\s*(l|lt|litre)\b/.test(lower)) return 'JUG_19L';
  if (/\b0[.,]5\s*(l|lt|litre)\b/.test(lower)) return 'CASE_05L';
  // "500 ml" / "12'li koli" kalıbı.
  if (/(500\s*ml)/.test(lower) && /(koli|'\s*lu|x)/.test(lower)) return 'CASE_05L';
  return null;
}
